import React, { useLayoutEffect, useRef } from "react";
import { Trash2, Save, Upload, Printer, LogOut } from "lucide-react";

const LOGO_URL =
  "https://raw.githubusercontent.com/atpcummings-code/swordpoint-data/refs/heads/main/swordpointlogo.png";

const PILL_STYLE = {
  background: "#FAF6ED",
  border: "1px solid #8A734C",
  color: "#16120E",
  fontFamily: "'Cinzel', 'EB Garamond', serif",
  fontVariant: "small-caps",
  fontWeight: 600,
  letterSpacing: "0.04em",
};

function PillButton({ testId, onClick, icon: Icon, children, minimal, crimson }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        ...PILL_STYLE,
        borderRadius: "6px",
        ...(minimal ? null : { boxShadow: "0 2px 4px rgba(22,18,14,0.25)" }),
        ...(crimson ? { color: "#9E2A2B", borderColor: "#9E2A2B" } : null),
        ...(minimal
          ? {
              background: "transparent",
              borderColor: "transparent",
              color: "#6B583E",
            }
          : null),
      }}
      className={`inline-flex items-center gap-2 text-sm whitespace-nowrap transition-[filter,transform,color] hover:brightness-95 active:scale-[0.98] px-4 py-1.5 ${minimal ? "hover:!text-[#9E2A2B]" : crimson ? "" : "hover:!text-[#219cd7]"}`}
    >
      <Icon size={15} /> {children}
    </button>
  );
}

export function AppHeaderBar({ email, onClear, onSave, onLoad, onExport, onSignOut, children }) {
  const barRef = useRef(null);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const update = () => root.style.setProperty("--sp-topbar-h", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={barRef}
      data-testid="app-header-bar"
      className="no-print sticky top-0 z-[60]"
      style={{
        background: "#EDE3CB",
        borderBottom: "1px solid #4A3A28",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-end justify-between gap-6 flex-wrap">
        <div className="flex flex-col items-start gap-1 self-center">
          <a href="/" data-testid="header-logo-link" aria-label="Swordpoint home">
            <img
              data-testid="header-logo"
              src={LOGO_URL}
              alt="Swordpoint"
              className="h-12 w-auto object-contain"
            />
          </a>
          <span
            data-testid="header-subtitle"
            className="text-[14.67px] leading-none text-left"
            style={{
              color: "#219cd7",
              fontFamily: "'Cinzel', 'EB Garamond', serif",
              fontVariant: "small-caps",
              textShadow: "0 1px 2px rgba(22,18,14,0.35)",
            }}
          >
            Army List Builder
          </span>
        </div>
        <div className="flex flex-col items-end gap-1 self-center">
          {email && (
            <span
              data-testid="header-user-email"
              className="text-[11px] leading-none text-right"
              style={{ color: "#6B583E", fontFamily: "'EB Garamond', 'Cinzel', serif" }}
            >
              User: {email}
            </span>
          )}
          <div className="flex items-stretch gap-3 flex-wrap justify-end">
            <PillButton testId="clear-roster-btn" onClick={onClear} icon={Trash2} crimson>
              Clear Roster
            </PillButton>
            <PillButton testId="save-army-btn" onClick={onSave} icon={Save}>
              Save Army
            </PillButton>
            <PillButton testId="load-army-btn" onClick={onLoad} icon={Upload}>
              Load Army
            </PillButton>
            <PillButton testId="export-pdf-btn" onClick={onExport} icon={Printer}>
              Export PDF
            </PillButton>
            <span
              data-testid="header-divider"
              aria-hidden="true"
              className="w-px self-stretch"
              style={{ background: "#8A734C" }}
            />
            <PillButton testId="logout-btn" onClick={onSignOut} icon={LogOut} minimal>
              Sign Out
            </PillButton>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
